import { ReactElement, useState, useMemo } from 'react';
import { CircularProgress } from '@mui/material';
import { compact } from 'lodash';

import { useRequest } from '~hooks/index';
import { Nullable, SelectOption } from '~globals/types';
import { getAdmingBillingListBillingById } from '~services/admin/billingList/billing';
import { AdminBillingListBillingBase } from '~services/admin/billingList/billing/types';
import { getServiceTypesCompany } from '~services/enumerations';

import { DialogBillingListBillingFormProps } from './types';
import DialogBillingListBillingFormContent from './Content';
import {
  DialogBillingListBillingFormContainer,
  DialogBillingListBillingFormLoadingContainer,
} from './styles';

const DialogBillingListBillingForm = ({
  open,
  onClose,
  billingListId,
  billingId,
  defaultBillingListCode,
  onSuccessSubmit,
}: DialogBillingListBillingFormProps): ReactElement => {
  const [billingData, setBillingData] =
    useState<Nullable<AdminBillingListBillingBase>>(null);

  const [, loadingGetBillingData] = useRequest({
    request: getAdmingBillingListBillingById,
    initialLoading: Boolean(billingId),
    isValidToRequest: Boolean(billingId),
    payload: { billingListId, billingId: billingId as string },
    withPostSuccess: (response) => {
      const responseBillingData = response.data
        ?.data as AdminBillingListBillingBase;

      setBillingData(responseBillingData);
    },
  });

  const [serviceTypes, setServicesTypes] = useState<SelectOption[]>([]);
  const [, loadingGetServiceTypes] = useRequest({
    request: getServiceTypesCompany,
    payload: null,
    withPostSuccess: (response) => {
      const servicesListResponse = compact(
        response.data?.data.results as string[],
      );

      const currentServiceTypes: SelectOption[] = servicesListResponse.map(
        (service) => ({
          value: service,
          label: service,
        }),
      );

      setServicesTypes(currentServiceTypes);
    },
  });

  const loading = useMemo(
    () => loadingGetBillingData || loadingGetServiceTypes,
    [loadingGetBillingData, loadingGetServiceTypes],
  );

  return (
    <DialogBillingListBillingFormContainer open={open}>
      {loading && (
        <DialogBillingListBillingFormLoadingContainer>
          <CircularProgress color="primary" disableShrink size={80} />
        </DialogBillingListBillingFormLoadingContainer>
      )}

      {!loading && (
        <DialogBillingListBillingFormContent
          billingListId={billingListId}
          billingId={billingId}
          defaultBillingListCode={defaultBillingListCode}
          data={billingData}
          serviceTypes={serviceTypes}
          onClose={onClose}
          onSuccessSubmit={onSuccessSubmit}
        />
      )}
    </DialogBillingListBillingFormContainer>
  );
};

export default DialogBillingListBillingForm;
